/**
 * @fileoverview Today's picture, chosen in the profile (Display options): what
 * shows now, small, and a button that opens the photo chooser (a photo of
 * the garden, or back to the season's painting). It used to be a pencil on
 * the picture itself. Only the garden's owner changes it (B37): a member
 * tending someone else's garden sees the picture and, in place of the button,
 * "Only the garden's owner can change this." (useOwnerOnly; the server refuses
 * a member's change 403 OWNER_ONLY).
 *
 * Where: the profile's Display options (src/user/components/ProfilePreferences.tsx).
 * API: GET /today?zip= (the same query and key as Today: the garden's postcode and its own day, both the
 * server's, from the garden place, useGardenPostcode).
 * A Google Photos pick for this picture that came back after the dialog closed
 * (B40) is handed over by the app's frame (shared/lib/lateHandoff); the dialog
 * opens with it previewed, and it is saved only on Save, as a chosen one is.
 * Words (en/es): profile.preferences.todayPicture.change, garden.ownerOnly.
 *
 * @module TodayPictureSetting
 */

import { retryRule } from '@/config/queryRetry';
import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/shared/components/ui/button';
import { useGardenPostcode } from '@/shared/hooks/useGardenPostcode';
import { useOwnerOnly } from '@/shared/hooks/useOwnerOnly';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { LATE_HANDOFF_HOLD_MS, onLate, takeLate } from '@/shared/lib/lateHandoff';
import { QUERY_STALE_TIME_MS } from '@/config/queryClient';
import { TODAY_QUERY_KEY, TODAY_QUERY_RETRIES } from '@/config/tunables/today';
import { getToday } from '@/shared/api/today';
import { PAINTING_ASPECT, todayPicture } from '../lib/paintings';
import { TodayPhotoDialog } from './TodayPhotoDialog';

/** The picture now, and a way to change it. */
export const TodayPictureSetting: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  const queryClient = useQueryClient();
  // The garden's postcode and its own day, the server's: the same day Today reads, so each sees the other's change.
  const { zip, known, date: today } = useGardenPostcode();
  const { data: day } = useQuery({
    queryKey: [TODAY_QUERY_KEY, zip, today],
    queryFn: () => getToday(zip),
    enabled: known && today.length > 0,
    staleTime: QUERY_STALE_TIME_MS,
    retry: retryRule(TODAY_QUERY_RETRIES),
  });
  const [isEditing, setIsEditing] = useState(false);
  /** A Google Photos pick that came late for Today's picture (B40), opened in the dialog to be saved. */
  const [lateFile, setLateFile] = useState<File | null>(null);
  useEffect(() => {
    /** Takes the held photo, if there is one, and opens the dialog with it. */
    const take = (): void => {
      const file = takeLate('today', null, Date.now(), LATE_HANDOFF_HOLD_MS);
      if (file) {
        setLateFile(file);
        setIsEditing(true);
      }
    };
    take();
    return onLate(take);
  }, []);
  const { src, position } = todayPicture(day);
  // A member sees the owner's picture but cannot change it (B37).
  const { lockedReason } = useOwnerOnly();

  return (
    <div className="flex items-center gap-3">
      {src && (
        <img
          src={src}
          alt=""
          className="h-12 w-auto rounded border object-cover"
          style={{ aspectRatio: PAINTING_ASPECT, objectPosition: position }}
        />
      )}
      {lockedReason ? (
        <p className="text-sm text-muted-foreground">{lockedReason}</p>
      ) : (
        <Button type="button" variant="outline" disabled={!day} onClick={() => setIsEditing(true)}>
          {t('profile.preferences.todayPicture.change')}
        </Button>
      )}
      <TodayPhotoDialog
        isOpen={isEditing && !lockedReason}
        onClose={() => { setIsEditing(false); setLateFile(null); }}
        initialPhoto={lateFile}
        current={src}
        currentPosition={position}
        hasOwnPhoto={Boolean(day?.photo)}
        onChanged={() => void queryClient.invalidateQueries({ queryKey: [TODAY_QUERY_KEY] })}
      />
    </div>
  );
};
